import React, { useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames/bind";
import styles from "./CustomerCare.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateLeft, faSync } from "@fortawesome/free-solid-svg-icons";
import { BASE_CUSTOMER_CARE_URL } from "~/helper/apiService";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";

const cx = classNames.bind(styles);

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("vi-VN");
}

function CareHistoryPage() {
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set());

  const totalItems = history?.length || 0;

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_CUSTOMER_CARE_URL}/history?limit=100`, {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setHistory(data.history || []);
    } catch (err) {
      showToast(`Lỗi khi lấy lịch sử: ${err.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = (batchId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  const grouped = useMemo(() => history || [], [history]);

  const handleUndo = (batchId) => {
    showModal({
      title: "Xóa (hoàn tác) lịch sử chăm sóc",
      message:
        "Bạn có chắc muốn xóa batch này không? Hệ thống sẽ đưa khách về trạng thái trước đó và xóa trên Excel/Sheet Status (không tạo thêm dòng).",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_CUSTOMER_CARE_URL}/history/undo`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ batchId }),
          });
          const data = await res.json();
          if (!res.ok || !data?.success) {
            throw new Error(data?.error || `HTTP ${res.status}`);
          }
          showToast(`Đã xóa/hoàn tác ${data.undone || 0} khách`, "success");
          fetchHistory();
        } catch (err) {
          showToast(`Lỗi khi xóa/hoàn tác: ${err.message}`, "error");
        }
        return true;
      },
      onCancel: null,
    });
  };

  return (
    <div className={cx("customer-care")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>Lịch sử chăm sóc</h2>
        <div className={cx("actions")}>
          <button className={cx("button", "refresh-button")} onClick={fetchHistory} disabled={loading}>
            <FontAwesomeIcon icon={faSync} spin={loading} /> Làm mới
          </button>
        </div>
      </div>

      <div className={cx("count-info")}>Tổng batch: {totalItems}</div>

      <div className={cx("table-container")}>
        {grouped.length === 0 ? (
          <div className={cx("no-data")}>Chưa có lịch sử</div>
        ) : (
          <table className={cx("table")}>
            <thead>
              <tr>
                <th className={cx("th")}>Thời gian</th>
                <th className={cx("th")}>Trạng thái</th>
                <th className={cx("th")}>Nhân viên</th>
                <th className={cx("th")}>Ghi chú</th>
                <th className={cx("th")}>Số khách</th>
                <th className={cx("th")}></th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => {
                const isOpen = expanded.has(g.batchId);
                return (
                  <React.Fragment key={g.batchId}>
                    <tr className={cx("tr")} onClick={() => toggleExpand(g.batchId)} style={{ cursor: "pointer" }}>
                      <td className={cx("td")}>{formatDateTime(g.createdAt)}</td>
                      <td className={cx("td")}>{g.careStatus}</td>
                      <td className={cx("td")}>{g.careStaff || "-"}</td>
                      <td className={cx("td")}>{g.careNote || "-"}</td>
                      <td className={cx("td")}>{g.total}</td>
                      <td className={cx("td")} style={{ textAlign: "right" }}>
                        <button
                          className={cx("button")}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUndo(g.batchId);
                          }}
                        >
                          <FontAwesomeIcon icon={faRotateLeft} /> Xóa
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className={cx("tr")}>
                        <td className={cx("td")} colSpan={6}>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {(g.phones || []).map((p) => (
                              <span
                                key={p}
                                style={{
                                  padding: "4px 8px",
                                  border: "1px solid #e0e0e0",
                                  borderRadius: 6,
                                  background: "#fafafa",
                                }}
                              >
                                {p}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default CareHistoryPage;

